import { useEffect, useState } from "react";

const TAB_DEFINITIONS = [
  {
    key: "strategy",
    label: "Strategy",
  },
  {
    key: "simulator",
    label: "Simulator",
  },
  {
    key: "evaluation",
    label: "Evaluation",
  },
  {
    key: "ablations",
    label: "Ablations",
  },
];

export function MethodologyPage({ client }) {
  const [state, setState] = useState({ loading: true });
  const [activeTab, setActiveTab] = useState(TAB_DEFINITIONS[0].key);

  useEffect(() => {
    if (!client) {
      setState({ loading: true });
      return undefined;
    }
    let active = true;
    setState({ loading: true });
    client
      .methodology()
      .then((document) => {
        if (active) {
          setState({ document, loading: false });
        }
      })
      .catch((error) => {
        if (active) {
          setState({ error: error.message, loading: false });
        }
      });
    return () => {
      active = false;
    };
  }, [client]);

  if (state.loading) {
    return <div className="methodology-page">Loading methodology.</div>;
  }
  if (state.error) {
    return <div className="methodology-page notice">{state.error}</div>;
  }

  const document = state.document || {};
  const tabs = methodologyTabs(document.sections || []);
  const selectedTab = tabs.find((tab) => tab.key === activeTab) || tabs[0];
  return (
    <article className="methodology-page">
      <header className="methodology-hero">
        <h1>{document.title || "Methodology"}</h1>
        {document.lede ? (
          <p className="methodology-lede">{document.lede}</p>
        ) : null}
      </header>

      <nav aria-label="Methodology sections" className="methodology-tabs">
        {tabs.map((tab) => (
          <button
            aria-pressed={tab.key === selectedTab.key}
            className={
              tab.key === selectedTab.key
                ? "methodology-tab methodology-tab-active"
                : "methodology-tab"
            }
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="methodology-tab-panel">
        {selectedTab.sections.map((section) => (
          <MethodologySection key={section.title} section={section} />
        ))}
      </div>
    </article>
  );
}

function methodologyTabs(sections) {
  const tabs = TAB_DEFINITIONS.map((definition) => {
    const tabSections = sections.filter((section) => section.tab === definition.key);
    return {...definition, sections: tabSections};
  }).filter((tab) => tab.sections.length);
  const knownTabs = new Set(TAB_DEFINITIONS.map((definition) => definition.key));
  const otherSections = sections.filter((section) => !knownTabs.has(section.tab));
  if (otherSections.length) {
    tabs.push({
      key: "other",
      label: "Other",
      sections: otherSections,
    });
  }
  return tabs.length
    ? tabs
    : [{key: "methodology", label: "Methodology", sections}];
}

function MethodologySection({ section }) {
  return (
    <section className={`methodology-section ${section.class_name || ""}`}>
      <h2>{section.title}</h2>
      {(section.paragraphs || []).map((paragraph) => (
        <p key={paragraph}>{paragraph}</p>
      ))}
      {section.cards?.length ? (
        <div className="methodology-grid">
          {section.cards.map((card) => (
            <MetricCard card={card} key={card.label} />
          ))}
        </div>
      ) : null}
      {section.list?.length ? (
        <ul>
          {section.list.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
      {section.table ? <MethodologyTable table={section.table} /> : null}
      {(section.paragraphs_after || []).map((paragraph) => (
        <p key={paragraph}>{paragraph}</p>
      ))}
    </section>
  );
}

function MetricCard({ card }) {
  return (
    <div className="methodology-metric">
      {card.label}
      <strong>{card.value}</strong>
      {card.text ? <p>{card.text}</p> : null}
    </div>
  );
}

function MethodologyTable({ table }) {
  return (
    <div className="methodology-table-wrap">
      <table>
        <thead>
          <tr>
            {(table.headers || []).map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(table.rows || []).map((row) => (
            <tr key={row.join("|")}>
              {row.map((cell, index) => (
                <td key={`${row[0]}-${index}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
